import { useEffect } from 'react'
import type { SmpteController } from '../BitmovinPlayer/types'

/**
 * Angel Player keyboard shortcuts hook.
 *
 * This hook centralizes all keyboard shortcut behavior for the Angel video player.
 * It is intentionally kept separate from `AngelPlayerContext` so that:
 * - Keyboard concerns are isolated from playback / tracking state management
 * - The same behavior can be reused or evolved independently of the context
 * - The rules for when we *do not* capture keys are clearly documented in one place
 *
 * Behavior:
 * - Listens for `keydown` on `window`, but only reacts when:
 *   - There is an active `SmpteController`
 *   - The event target is inside the `.angel-player` container
 *   - The target is **not** an input/textarea/contenteditable
 *   - The device has a "fine" pointer (desktop; avoids hijacking mobile/touch)
 * - After any seek, it calls `onSeek` so callers can track analytics / state transitions.
 */
interface UsePlayerKeyboardShortcutsOptions {
  controller?: SmpteController
  onSeek: () => void
  /**
   * Optional duration of the current video in seconds.
   * When provided, right-arrow seeks will be clamped to this value.
   */
  durationSeconds?: number
}

/**
 * Registers global keyboard shortcuts for the Angel player while the given
 * controller is active and the user has interacted with the player.
 *
 * This is intentionally a `window`-level listener so that users can seek
 * even when focus is on non-focusable regions inside the player UI. To
 * avoid surprising UX, we strictly scope handling to events that originate
 * within the Angel player container and skip text-entry elements.
 */
export function usePlayerKeyboardShortcuts({
  controller,
  onSeek,
  durationSeconds,
}: UsePlayerKeyboardShortcutsOptions): void {
  useEffect(() => {
    // Only attach keyboard controls when we have an active controller
    if (!controller) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const isEligible = isKeyboardEventEligible(event)
      if (isEligible) handlePlayerKeyDown(event, controller, onSeek, durationSeconds)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [controller, onSeek, durationSeconds])
}

function isKeyboardEventEligible(event: KeyboardEvent): boolean {
  // Only handle on desktop (not mobile/touch devices)
  const isDesktop = window.matchMedia('(pointer: fine)').matches
  if (!isDesktop) return false

  // Ensure the Angel player is present on the page.
  const angelPlayerElement = document.querySelector<HTMLElement>('.angel-player')
  if (!angelPlayerElement) return false

  const target = event.target as HTMLElement | null
  if (!target) return false

  // Don't handle if user is typing in an input field
  if (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable ||
    target.closest('input, textarea, [contenteditable]')
  ) {
    return false
  }

  return true
}

function handlePlayerKeyDown(
  event: KeyboardEvent,
  controller: SmpteController,
  onSeek: () => void,
  durationSeconds?: number,
): void {
  // Handle left/right arrow keys for seeking
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    const currentTime = controller.getCurrentTime()
    const newTime = Math.max(0, currentTime - 10)
    controller.seek(newTime)
    onSeek()
    return
  }

  if (event.key === 'ArrowRight') {
    event.preventDefault()
    const currentTime = controller.getCurrentTime()
    const proposedTime = currentTime + 10
    const newTime = typeof durationSeconds === 'number' ? Math.min(durationSeconds, proposedTime) : proposedTime
    controller.seek(newTime)
    onSeek()
    return
  }

  if (event.key === ' ' || event.key === 'Spacebar') {
    // Toggle play / pause with Space, matching common video player behavior
    event.preventDefault()
    if (controller.isPlaying()) controller.pause()
    else controller.play()
  }
}
